import { PropsWithChildren } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { compactArray } from '@mews/fp';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { Link, Body, Tag } from '@optimus-web/core';
import { UserProfileResult } from '@mews-commander/data-access';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { UserTableActions } from '../../UserTableActions';
import { UserTableAvatar } from '../../UserTableAvatar';
import { useUserContext } from '../../../context';
import { useUserDetailContext } from '../../../context/userDetailContext';
import { UserType } from '../../../types';
import { useDisplaySourceColumn } from '../../../hooks/useDisplaySourceColumn';
import { UserStatusTag } from '../../UserStatusTag';
import { SecurityStatus } from '../../UserDetail/UserDetailContent/SecurityStatus';

interface UseColumnsOptions {
    onRowDelete: (id: string) => void;
}

const columnHelper = createColumnHelper<UserProfileResult>();

const PropertiesCount = ({ userProfile, ...props }: PropsWithChildren<{ userProfile: UserProfileResult }>) => {
    const { openUserDetail } = useUserDetailContext();

    const openSidePanel = () => openUserDetail({ userId: userProfile.userId, properties: true });

    return <Link variant="basic" as="button" type="button" onClick={openSidePanel} {...props} />;
};

export const useColumns = ({ onRowDelete }: UseColumnsOptions) => {
    const { userType } = useUserContext();
    const displaySourceColumn = useDisplaySourceColumn();

    return compactArray([
        columnHelper.accessor('fullName', {
            id: 'name',
            header: () => <LocalizedText textKey={TextKey.Name} />,
            cell: (props) => <UserTableAvatar userProfile={props.row.original} />,
            enableSorting: true,
            meta: {
                width: 300,
            },
        }),
        ...(userType === UserType.Properties
            ? [
                  columnHelper.accessor('departments', {
                      id: 'department',
                      header: () => <LocalizedText textKey={TextKey.Department} />,
                      cell: (props) => {
                          const value = props.getValue();
                          return value ? (
                              <Body size="small" ellipsis maxLines={1}>
                                  {value}
                              </Body>
                          ) : (
                              UnicodeEntity.Dash
                          );
                      },
                      enableSorting: true,
                  }),
              ]
            : [
                  columnHelper.accessor('superiorName', {
                      header: () => <LocalizedText textKey={TextKey.Supervisor} />,
                      cell: (props) => {
                          const value = props.getValue();
                          return value ? (
                              <Body size="small" ellipsis maxLines={1}>
                                  {value}
                              </Body>
                          ) : (
                              UnicodeEntity.Dash
                          );
                      },
                      enableSorting: true,
                  }),
              ]),
        columnHelper.accessor('roles', {
            header: () => <LocalizedText textKey={TextKey.Roles} />,
            cell: (props) => {
                const value = props.getValue();
                return value ? (
                    <Body size="small" ellipsis maxLines={1}>
                        {value}
                    </Body>
                ) : (
                    UnicodeEntity.Dash
                );
            },
            enableSorting: true,
        }),
        columnHelper.accessor('properties', {
            header: () => <LocalizedText textKey={TextKey.PortfolioPropertyPlural} />,
            enableSorting: true,
            cell: (props) => {
                const value = props.getValue();
                return userType === UserType.Portfolio ? (
                    TextKey.All
                ) : (
                    <PropertiesCount userProfile={props.row.original}>{value}</PropertiesCount>
                );
            },
        }),
        columnHelper.accessor('lastLogIn', {
            header: () => <LocalizedText textKey={TextKey.LastLogIn} />,
            cell: (props) => {
                const value = props.getValue();
                return value == null ? UnicodeEntity.Dash : <LocalizedDateFromUtc utcString={value} />;
            },
            enableSorting: true,
        }),
        columnHelper.accessor('status', {
            header: () => <LocalizedText textKey={TextKey.Status} />,
            cell: (props) => {
                const status = props.getValue();
                return <UserStatusTag status={status} />;
            },
            enableSorting: true,
        }),
        displaySourceColumn
            ? columnHelper.accessor('isScimProvisioned', {
                  header: () => <LocalizedText textKey={TextKey.Source} />,
                  cell: (props) => {
                      const source = props.getValue();
                      return <Tag variant="basicSubtle">{source ? lt(TextKey.IDP) : lt(TextKey.Mews)}</Tag>;
                  },
                  enableSorting: true,
              })
            : null,
        columnHelper.accessor('securityFeatures', {
            header: () => <LocalizedText textKey={TextKey.Authentication} />,
            cell: ({ getValue }) => <SecurityStatus securityFeatures={getValue()} />,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            meta: { width: 100, align: 'end' },
            cell: (props) => (
                <UserTableActions
                    userProfile={props.row.original}
                    onSuccess={() => onRowDelete(props.row.original.userId)}
                />
            ),
        }),
    ]);
};
